import React, { useState, useMemo } from "react";
import Highstock from "highcharts/highstock";
import ReactHighcharts from "react-highcharts";
import { DS_CHART_COLORS, DS_CHART_TOKENS, withDsChartTheme } from './../../utils/chartTheme';
import Skeleton from "react-loading-skeleton";
import { RoundedSelect } from "../tailwind/atoms";
import moment from "moment";
import { Select } from "antd";
import OpdIcon from "./OpdIcon";

const Option  = Select.Option;
const Charts = withDsChartTheme(ReactHighcharts.withHighcharts(Highstock));
const COLORS = DS_CHART_COLORS;

const BenefitsTrends = ({
  data = {},
  loading = false,
  trendKeyMap = {
    "Week-on-Week": "week_on_week",
    "Month-on-Month": "month_on_month",
  },
  selectedTrend = "Week-on-Week",
  servicesMap = {},
  currentService = '',
  handleServiceChange = () => {}
}) => {
  const [metricType, setMetricType] = useState("amount");
  const [selectedOption, setSelectedOption] = useState(selectedTrend);
  const [selectedBenefits, setSelectedBenefits] = useState([]);

  const trendData = data?.[trendKeyMap[selectedOption]] || {};
  const allBenefits = Object.keys(trendData || {});

  React.useEffect(() => {
    if (allBenefits.length && selectedBenefits.length === 0) {
      setSelectedBenefits(allBenefits.slice(0, 8));
    }
  }, [allBenefits]);

  const chartConfig = useMemo(() => {
    if (!trendData || Object.keys(trendData).length === 0) return null;

    const benefitKeys = selectedBenefits.length
      ? selectedBenefits
      : allBenefits;

    const categoriesSet = new Set();
    benefitKeys.forEach((b) => {
      (trendData[b]?.labels || []).forEach((l) => categoriesSet.add(l));
    });
    const categories = Array.from(categoriesSet).sort((a, b) => {
      const ma = moment(a, "DD-MMM").month();
      const mb = moment(b, "DD-MMM").month();
      let diff = ma - mb;
      if (diff > 6) diff -= 12;
      else if (diff < -6) diff += 12;
      if (diff !== 0) return diff;
      return moment(a, "DD-MMM").date() - moment(b, "DD-MMM").date();
    });

    const series = benefitKeys.map((benefit, idx) => {
      const dataset = trendData[benefit]?.[metricType] || {};
      const points = categories.map((c) => dataset[c] ?? null);
      return {
        name: benefit,
        data: points,
        color: COLORS[allBenefits.indexOf(benefit) % COLORS.length],
        lineWidth: benefitKeys.length > 6 ? 1.5 : 2.5,
        marker: {
          enabled: benefitKeys.length <= 10,
          radius: benefitKeys.length > 10 ? 2 : 4,
        },
      };
    });

    return {
      chart: { type: "line", height: 450, zoomType: "x" },
      title: { text: "" },
      credits: { enabled: false },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: { fontSize: "11px", color: DS_CHART_TOKENS.gray700, fontFamily: "Inter" },
        navigation: { activeColor: DS_CHART_TOKENS.semBlue400, animation: true },
        maxHeight: 80,
      },
      xAxis: {
        categories,
        title: { text: selectedOption },
        labels: {
          rotation: categories.length > 6 ? -45 : 0,
          style: { fontSize: "10px", color: DS_CHART_TOKENS.gray600, fontFamily: "JetBrains Mono" },
        },
      },
      yAxis: {
        title: {
          text:
            metricType === "volume" ? "Number of Claims" : "Amount (₹)",
        },
        allowDecimals: false,
        gridLineDashStyle: "Dash",
        labels: { style: { color: DS_CHART_TOKENS.gray600, fontFamily: "JetBrains Mono", fontSize: "10px" } },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          return `
            <div style="padding:2px 0;color:var(--ink);font-family:Inter,sans-serif;font-size:12px">
              <span style="font-family:JetBrains Mono,monospace;color:var(--gray-600);font-size:11px">${this.x}</span><br/>
              ${this.points
                .map(
                  (p) =>
                    `<span style="color:${p.color}">●</span> ${
                      p.series.name
                    }: <strong style="font-family:JetBrains Mono,monospace;color:var(--ink)">${
                      metricType === "amount"
                        ? `₹${new Intl.NumberFormat("en-IN", {
                            maximumFractionDigits: 0,
                          }).format(p.y)}`
                        : p.y
                    }</strong>`
                )
                .join("<br/>")}
            </div>
          `;
        },
      },
      series,
    };
  }, [data, metricType, selectedOption, selectedBenefits]);

  const benefitKeys = selectedBenefits.length ? selectedBenefits : allBenefits;
  const latestCategories = Array.from(
    benefitKeys.reduce((set, benefit) => {
      (trendData[benefit]?.labels || []).forEach((label) => set.add(label));
      return set;
    }, new Set())
  );
  const latestPeriod = latestCategories[latestCategories.length - 1];
  const latestTotal = latestPeriod
    ? benefitKeys.reduce((sum, benefit) => sum + Number(trendData[benefit]?.[metricType]?.[latestPeriod] || 0), 0)
    : null;
  const latestValue = latestTotal == null
    ? "—"
    : metricType === "amount"
      ? `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(latestTotal)}`
      : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(latestTotal);

  return (
    <div className="rounded-lg border border-gray-300 bg-white p-6 shadow-sm">
      {/* Header */}
      <div className="flex justify-between items-start mb-6 gap-6">
        <div className="flex items-start gap-3">
          <OpdIcon name="trends" className="h-6 w-6 rounded border border-blue-200 bg-blue-100 p-1.5 text-blue-700" />
          <div>
            <h1 className="mb-2 text-base font-fig-semibold leading-none text-gray-900">Benefit-wise Trends</h1>
            <div className="font-jetbrains-mono text-3xl tabular-nums text-gray-900">{latestValue}</div>
            {latestPeriod && <div className="mt-1 text-xs font-fig-normal text-gray-600">Latest selected total · {latestPeriod}</div>}
          </div>
        </div>

        <div className="flex gap-4 items-center">
          {/* Metric toggle */}
          <div className="flex space-x-1 overflow-x-auto rounded-md border border-gray-300 bg-gray-200 p-1">
            {["volume", "amount"].map((type) => (
              <button
                key={type}
                onClick={() => setMetricType(type)}
                className={`rounded-sm px-3 py-1.5 text-sm font-fig-semibold transition-all duration-200
                  ${
                    metricType === type
                      ? "rounded-md bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-700"
                  }`}
              >
                {type === "volume" ? "Count" : "Value (₹)"}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-4 items-center">
          {/* Service dropdown */}
          <RoundedSelect
            listClasses="w-34"
            selectOptions={Object.values(servicesMap)}
            selectValues={Object.keys(servicesMap).map((_, i) => i)}
            curvalue={servicesMap[currentService] || 'Overall'}
            selectHandler={(index) =>
              handleServiceChange('benefit_trends', Object.keys(servicesMap)[index])
            }
          />

          {/* Trend dropdown */}
          <RoundedSelect
            listClasses="w-44"
            selectOptions={Object.keys(trendKeyMap)}
            selectValues={Object.keys(trendKeyMap).map((_, i) => i)}
            curvalue={selectedOption}
            selectHandler={(index) =>
              setSelectedOption(Object.keys(trendKeyMap)[index])
            }
          />
        </div>
      </div>
      <div className="mb-4 ml-16 flex w-11/12 flex-col items-end gap-4">
          {/* Benefit selector */}
          <Select
            mode="multiple"
            allowClear
            placeholder="Select Benefits"
            value={selectedBenefits}
            onChange={(vals) => setSelectedBenefits(vals)}
            maxTagCount="responsive"
            tagRender={({ label, value, closable, onClose }) => {
              const idx = allBenefits.indexOf(value);
              const color = COLORS[idx % COLORS.length];

              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "var(--paper)",
                    border: `1.5px solid ${color}`,
                    borderRadius: "14px",
                    padding: "2px 6px",
                    margin: "2px",
                    fontSize: "13px",
                    color: "var(--gray-700)",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: color,
                      marginRight: "6px",
                    }}
                  />
                  {label}
                  {closable && (
                    <span
                      onClick={onClose}
                      style={{
                        marginLeft: "6px",
                        color: "var(--gray-600)",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      ✕
                    </span>
                  )}
                </div>
              );
            }}
          >
            {allBenefits.map((b, idx) => {
              const color = COLORS[idx % COLORS.length];
              return (
                <Option key={b} value={b}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: color,
                      }}
                    />
                    {b}
                  </div>
                </Option>
              );
            })}
          </Select>
      </div>

      {/* Chart */}
      {loading ? (
        <Skeleton width="100%" height={400} />
      ) : chartConfig ? (
        <Charts config={chartConfig} />
      ) : (
        <div className="w-full h-96 flex flex-col items-center justify-center text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-10 w-10 mb-2 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 3v18h18M3 9h18M9 21V9m6 12V9"
            />
          </svg>
          No data available
        </div>
      )}
    </div>
  );
};

export default BenefitsTrends;
